import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE } from '../../config/constants';

enum NetworkErrorType {
  CONNECTION_TIMEOUT = 'connection_timeout',
  RECEIVE_TIMEOUT = 'receive_timeout',
  SEND_TIMEOUT = 'send_timeout',
}

class NetworkConnectionError extends Error {
  constructor() {
    super('Network connection error');
    this.name = 'NetworkConnectionError';
  }
}

export interface RequestParameters {
  baseApiUrl: string;
  data?: any;
  mustEndWithSlash?: boolean;
  queryParameters?: Record<string, any>;
  additionalHeaders?: Record<string, string>;
  timeout?: number;
}

export abstract class ApiRepository {
  axiosInstance: AxiosInstance;
  constructor(axiosInstance: any) {
    this.axiosInstance = axiosInstance;
  }

  private validateStatus(code: number | null): boolean {
    if (code === null) {
      return false;
    }
    // You can customize your status code validation here
    return code < 500;
  }

  private handleConnectionError(err: any): void {
    if (err.isAxiosError) {
      if (
        err.code === NetworkErrorType.CONNECTION_TIMEOUT ||
        err.code === NetworkErrorType.RECEIVE_TIMEOUT ||
        err.code === NetworkErrorType.SEND_TIMEOUT
      ) {
        // this.networkBloc.add(NetworkConnectionLost());
        throw new NetworkConnectionError();
      }
    }
    throw err;
  }

  public async sendRequest<T>(
    method: string,
    path: string,
    options: RequestParameters = {
      baseApiUrl: API_BASE,
      mustEndWithSlash: true,
      data: undefined,
    },
  ): Promise<AxiosResponse<any>> {
    // Your function implementation here

    // Make sure to adjust the config retrieval based on your project structure
    // const cacheOptions: CacheOptions = getCacheOptions(
    //   config,
    //   use_body_as_cache_key,
    // );
    const splitPath = path.split('?');
    let updatedPath = splitPath[0];
    if (!updatedPath.endsWith('/') && options.mustEndWithSlash) {
      updatedPath += '/';
    }

    if (splitPath.length > 1) {
      updatedPath += '?' + splitPath[1];
    }
    const axiosOptions: AxiosRequestConfig = {
      method: method,
      headers: options.additionalHeaders,
      validateStatus: (code) => this.validateStatus(code),
    };

    try {
      const response = await this.axiosInstance.request<T>({
        url: `${options.baseApiUrl}${updatedPath}`,
        method: axiosOptions.method,
        headers: axiosOptions.headers,
        params: options.queryParameters,
        data: options.data,
        validateStatus: axiosOptions.validateStatus,
      });
      return response;
    } catch (error) {
      this.handleConnectionError(error);
      throw error; // Make sure to rethrow the error to maintain the rejected Promise
    }
  }
}
